import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { money } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { CurrencyDollar, Clock, Warning, Users, Wallet, Scales, Signature } from "@phosphor-icons/react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Revenus perçus", value: money(stats.total_revenue), icon: CurrencyDollar, accent: "text-emerald-700" },
        { label: "En attente", value: money(stats.outstanding), icon: Clock, accent: "text-blue-700" },
        { label: "À payer", value: money(stats.to_pay), icon: Wallet, accent: "text-amber-600" },
        { label: "En retard", value: stats.overdue_count, icon: Warning, accent: "text-red-600" },
        { label: "Écarts en cours", value: stats.open_disputes ?? 0, icon: Warning, accent: (stats.open_disputes ?? 0) > 0 ? "text-red-600" : "text-foreground", to: "/ecarts", testid: "tile-disputes" },
        { label: "Contrats à échéance (30 j)", value: stats.expiring_contracts ?? 0, icon: Signature, accent: (stats.expiring_contracts ?? 0) > 0 ? "text-amber-600" : "text-foreground", to: "/contrats?echeance=1", testid: "tile-expiring-contracts" },
        { label: `Net à remettre ${stats.annual_year ?? ""}`.trim(), value: money(stats.annual_net_remit ?? 0), icon: Scales, accent: "text-primary", to: "/rapports", testid: "tile-annual-net" },
        { label: "Clients", value: stats.clients_count, icon: Users, accent: "text-foreground" },
      ]
    : [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Tableau de bord</h1>
        <p className="text-muted-foreground mt-1">Vue d'ensemble de votre facturation.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            data-testid={c.testid}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            onClick={c.to ? () => navigate(c.to) : undefined}
            role={c.to ? "button" : undefined}
            tabIndex={c.to ? 0 : undefined}
            onKeyDown={c.to ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(c.to); } } : undefined}
            className={`border border-border bg-card p-5 rounded-sm hover:-translate-y-1 transition-transform ${c.to ? "cursor-pointer hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40" : ""}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{c.label}</span>
              <c.icon size={18} className={c.accent} />
            </div>
            <div className={`mt-3 font-heading text-2xl font-bold tabular-nums ${c.accent}`}>{c.value}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border bg-card p-6 rounded-sm">
          <h2 className="font-heading text-lg font-bold mb-4">Revenus des 6 derniers mois</h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats?.monthly || []} margin={{ left: -10, right: 10, top: 10 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} width={60}
                tickFormatter={(v) => `${v} $`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, fontSize: 12, border: "1px solid hsl(var(--border))" }} />
              <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6 rounded-sm">
          <h2 className="font-heading text-lg font-bold mb-4">Factures récentes</h2>
          <div className="space-y-3" data-testid="recent-invoices">
            {(stats?.recent || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune facture pour le moment.</p>
            )}
            {(stats?.recent || []).map((inv) => (
              <div key={inv.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0">
                <div>
                  <div className="font-mono text-sm font-medium">{inv.invoice_number}</div>
                  <div className="text-xs text-muted-foreground">{inv.client?.name}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium tabular-nums">{money(inv.total)}</div>
                  <StatusBadge status={inv.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
